/**
 * Display formatters. Presentation only - the client derives no domain state
 * (docs/30-interfaces/frontend.md section 1).
 *
 * statusLabels / statusLabel / formatBytes are ported from the previous main.tsx:14-36.
 */
import { LIFECYCLE_DISPLAY, type DatasetLifecycleStatus, type LifecycleTone } from '@platform/contracts';
import type { CollectionType, Modality } from './contract';
import { getRealm, type Realm } from './api';

/**
 * Lifecycle presentation comes from @platform/contracts, beside the status list the API derives
 * from. The table used to live here and knew eight of the twelve statuses, so `certified`,
 * `evaluated`, `in_review` and `needs_fixes` rendered as grey badges with underscore-stripped
 * labels. An unknown value still degrades rather than throwing, but it can no longer be one the
 * server routinely sends.
 */
/**
 * The realm decides which reading of a state a person gets.
 *
 * A dataset waiting on an operator is their queue and the vendor's news: "Decision required" is
 * meaningless to someone who cannot act on it, and "Evaluated" tells an operator nothing about what
 * they owe. Defaults to the realm of the page being viewed, so the ordinary call site says nothing
 * about realms at all.
 */
export const statusLabel = (value: string, realm: Realm = getRealm()) => {
  const entry = LIFECYCLE_DISPLAY[value as DatasetLifecycleStatus];
  if (!entry) return value.replaceAll('_', ' ');
  return realm === 'vendor' ? (entry.vendorLabel ?? entry.label) : entry.label;
};

export const statusTone = (value: string, realm: Realm = getRealm()): LifecycleTone => {
  const entry = LIFECYCLE_DISPLAY[value as DatasetLifecycleStatus];
  if (!entry) return 'muted';
  return realm === 'vendor' ? (entry.vendorTone ?? entry.tone) : entry.tone;
};

export function formatBytes(value: number | string | null | undefined): string {
  const bytes = Number(value);
  if (value == null || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} ${bytes === 1 ? 'byte' : 'bytes'}`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit++;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unit]}`;
}

/** "412 h", with the thousands separator the mockups use ("1,284 h"). */
export function formatHours(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('en-US')} h`;
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value}%`;
}

/** "Jul 15" - the short form used in every table and feed. */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
}

/**
 * "Aug 30 14:32" — a day is not enough when several things happen within one.
 *
 * The pipeline history is a sequence, and rendered with `formatDay` every row of it read "Aug 30":
 * the same six characters against six different stages, so the panel showed an order without ever
 * showing that anything took time.
 */
export function formatMoment(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${formatDay(value)} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

/** "JUL 14" - the uppercase mono variant used on catalog cards. */
export const formatDayUpper = (value: string | null | undefined) => formatDay(value).toUpperCase();

/** "4:12" */
/**
 * `m:ss`, from a duration that is very rarely a whole number of seconds.
 *
 * This used to take `seconds % 60` and pad the result, which does nothing to a float and leaks the
 * arithmetic straight onto the screen: an episode of 118.85 s rendered as `1:58.849999999999994`,
 * and one of 9.87 s as `0:9.87` — unpadded, because `padStart` sees a four-character string and
 * leaves it alone. Durations come from frame counts divided by fps, or from an MCAP's start and end
 * timestamps, so a clean number is the exception rather than the rule.
 *
 * Rounded to the second, which is the resolution the column is read at.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Signed delta for stat tiles: "+312 h vs prior 30d", "-4 pts vs prior 30d". */
export function formatDelta(value: number, unit: string, suffix: string): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('en-US')} ${unit} ${suffix}`;
}

const MODALITY_LABELS: Record<Modality, string> = {
  ego: 'EGO',
  rgb_d: 'RGB-D',
  audio: 'AUDIO',
  teleop: 'TELEOP',
  multi_rgb: 'MULTI-RGB',
  depth: 'DEPTH',
  force_torque: 'F-T'
};

export const modalityLabel = (value: Modality) => MODALITY_LABELS[value] ?? value;

/** Catalog cards spell modalities out joined by "+": "EGO + RGB-D + AUDIO". */
export const modalityLine = (values: Modality[]) => (values.length ? values.map(modalityLabel).join(' + ') : '—');

const COLLECTION_LABELS: Record<CollectionType, string> = {
  in_the_wild: 'In-the-wild',
  human_demo: 'Human demo',
  teleoperation: 'Teleoperation',
  scripted: 'Scripted'
};

export const collectionLabel = (value: CollectionType | null) => (value ? COLLECTION_LABELS[value] : '—');

/** The em dash the mockups use for every empty cell. */
export const emptyDash = '—';

export const orDash = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? emptyDash : String(value);
